import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import './TitleBar.css'

import { chatSliceActions, selectCcCwd, selectCurrentConversationId } from '../../features/chats'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'

export const TitleBar = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [platform, setPlatform] = useState<string>('')
  const [isElectron, setIsElectron] = useState(false)
  const [isCompact, setIsCompact] = useState(false)
  const [isCompactLoading, setIsCompactLoading] = useState(false)

  const currentCwd = useAppSelector(selectCcCwd)
  const currentConversationId = useAppSelector(selectCurrentConversationId)

  useEffect(() => {
    const detectPlatform = async () => {
      if (window.electronAPI?.platformInfo?.get) {
        const info = await window.electronAPI.platformInfo.get()
        setPlatform(info.platform)
        setIsElectron(info.isElectron)

        // Add padding to app content on Windows
        if (info.isElectron && info.platform === 'win32') {
          document.body.classList.add('has-titlebar')
        }

        // Check initial compact mode state
        if (window.electronAPI?.window?.isCompact) {
          const compact = await window.electronAPI.window.isCompact()
          setIsCompact(compact)
        }
      }
    }
    detectPlatform()

    // Cleanup
    return () => {
      document.body.classList.remove('has-titlebar')
    }
  }, [])

  // Only show on Windows in Electron
  if (!isElectron || platform !== 'win32') {
    return null
  }

  const handleToggleCompact = async () => {
    if (!window.electronAPI?.window?.toggleCompact) return
    setIsCompactLoading(true)
    try {
      const result = await window.electronAPI.window.toggleCompact()
      if (result.success) {
        setIsCompact(result.compact)
      }
    } catch (err) {
      console.error('Failed to toggle compact mode:', err)
    } finally {
      setIsCompactLoading(false)
    }
  }

  const handleMinimize = () => {
    window.electronAPI?.window?.minimize()
  }

  const handleMaximize = () => {
    window.electronAPI?.window?.maximize()
  }

  const handleClose = () => {
    window.electronAPI?.window?.close()
  }

  const isChatPage = location.pathname.startsWith('/chat/')
  const canPickCwd = isChatPage && Boolean(currentConversationId) && Boolean(window.electronAPI?.dialog?.selectFolder)

  const handlePickCwd = async () => {
    if (!canPickCwd) return
    try {
      const result = await window.electronAPI?.dialog?.selectFolder()
      if (result?.success && result.path) {
        dispatch(chatSliceActions.ccCwdSet(result.path))
      }
    } catch (err) {
      console.error('Failed to select working directory from title bar:', err)
    }
  }

  return (
    <div className={`titlebar ${isChatPage ? 'titlebar-chat' : ''}`}>
      <div className='titlebar-drag-region'>
        <div
          style={
            { display: 'flex', alignItems: 'center', gap: '2px', WebkitAppRegion: 'no-drag' } as React.CSSProperties
          }
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4px',
              borderRadius: '6px',
              opacity: 0.8,
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => (
              (e.currentTarget.style.opacity = '1'),
              (e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)')
            )}
            onMouseLeave={e => (
              (e.currentTarget.style.opacity = '0.8'),
              (e.currentTarget.style.backgroundColor = 'transparent')
            )}
            title='Go Back'
          >
            <ChevronLeft size={16} strokeWidth={2} />
          </button>
          <button
            onClick={() => navigate(1)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4px',
              borderRadius: '6px',
              opacity: 0.8,
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => (
              (e.currentTarget.style.opacity = '1'),
              (e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)')
            )}
            onMouseLeave={e => (
              (e.currentTarget.style.opacity = '0.8'),
              (e.currentTarget.style.backgroundColor = 'transparent')
            )}
            title='Go Forward'
          >
            <ChevronRight size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
      <div className='titlebar-controls'>
        <button
          type='button'
          className='cwd-pill'
          onClick={handlePickCwd}
          disabled={!canPickCwd}
          style={{
            marginRight: '2px',
            marginTop: '4px',
            marginBottom: '4px',
            fontSize: '11px',
            lineHeight: '1', // tighter text box
            color: 'rgb(255, 255, 255)',
            maxWidth: '300px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'flex',
            alignItems: 'center',
            userSelect: 'none',
            cursor: canPickCwd ? 'pointer' : 'default',
            backgroundColor: 'rgba(255, 255, 255, 0.34)',
            borderRadius: '9999px',
            padding: '1px 12px', // was 0px 16px
            minHeight: '18px', // optional: force slimmer height
            border: 'none',
          }}
          title={
            canPickCwd
              ? currentCwd
                ? `${currentCwd} (click to change)`
                : 'Click to select a work folder'
              : currentCwd || 'Open a chat conversation to set work folder'
          }
        >
          {currentCwd ? (
            <span className='cwd-pill-text cwd-pill-text-scroll' style={{ fontFamily: 'monospace' }}>
              {currentCwd}
            </span>
          ) : (
            <span className='cwd-pill-text' style={{ fontStyle: 'italic', opacity: 1 }}>
              Select a work folder
            </span>
          )}
        </button>
        <button
          className='titlebar-button titlebar-compact'
          onClick={handleToggleCompact}
          disabled={isCompactLoading}
          aria-label={isCompact ? 'Disable Floating Mode' : 'Enable Floating Mode'}
          title={isCompact ? 'Disable Floating Mode' : 'Enable Floating Mode'}
        >
          {isCompactLoading ? (
            <svg width='10' height='10' viewBox='0 0 10 10' className='animate-spin'>
              <circle cx='5' cy='5' r='4' fill='none' stroke='currentColor' strokeWidth='1' strokeDasharray='12 6' />
            </svg>
          ) : isCompact ? (
            <svg width='10' height='10' viewBox='0 0 10 10'>
              <path fill='currentColor' d='M0 0v4h1V1h3V0H0zm6 0v1h3v3h1V0H6zM0 6v4h4V9H1V6H0zm9 0v3H6v1h4V6H9z' />
            </svg>
          ) : (
            <svg width='10' height='10' viewBox='0 0 10 10'>
              <path fill='currentColor' d='M3 0v1H1v2H0V0h3zm4 0h3v3H9V1H7V0zM0 7h1v2h2v1H0V7zm9 0h1v3H7V9h2V7z' />
            </svg>
          )}
        </button>
        <button
          className='titlebar-button titlebar-minimize'
          onClick={handleMinimize}
          aria-label='Minimize'
          title='Minimize'
        >
          <svg width='10' height='1' viewBox='0 0 10 1'>
            <rect fill='currentColor' width='10' height='1' />
          </svg>
        </button>
        <button
          className='titlebar-button titlebar-maximize'
          onClick={handleMaximize}
          aria-label='Maximize'
          title='Maximize'
        >
          <svg width='10' height='10' viewBox='0 0 10 10'>
            <path fill='currentColor' d='M0 0v10h10V0H0zm1 1h8v8H1V1z' />
          </svg>
        </button>
        <button className='titlebar-button titlebar-close' onClick={handleClose} aria-label='Close' title='Close'>
          <svg width='10' height='10' viewBox='0 0 10 10'>
            <path
              fill='currentColor'
              d='M1.207.293l-.914.914L4.086 5 .293 8.793l.914.914L5 5.914l3.793 3.793.914-.914L5.914 5l3.793-3.793-.914-.914L5 4.086 1.207.293z'
            />
          </svg>
        </button>
      </div>
    </div>
  )
}
